import type {CreateIssueFieldValueMutationMutation} from '@github-ui/server-persisted-queries/types/createIssueFieldValueMutation'
import type {UpdateIssueFieldValueMutationMutation} from '@github-ui/server-persisted-queries/types/updateIssueFieldValueMutation'
import type {Environment, RecordProxy, RecordSourceProxy, RecordSourceSelectorProxy} from 'relay-runtime'

import {ISSUE_FIELD_VALUES_PAGE_SIZE} from './constants'
import type {IssueFieldValueMutationInput, OptimisticIssueFieldValue} from './helpers'

type ConfirmedIssueFieldValue = NonNullable<
  NonNullable<CreateIssueFieldValueMutationMutation['createIssueFieldValue']>['issueFieldValue']
>
type ConfirmedUpdatedIssueFieldValue = NonNullable<
  NonNullable<UpdateIssueFieldValueMutationMutation['updateIssueFieldValue']>['issueFieldValue']
>
type ConfirmedFieldValue = ConfirmedIssueFieldValue | ConfirmedUpdatedIssueFieldValue

function replaceFieldValueInCollection(
  fieldValues: ReturnType<RecordSourceSelectorProxy['get']>,
  fieldId: string,
  newNode: NonNullable<ReturnType<RecordSourceSelectorProxy['get']>>,
) {
  if (!fieldValues || !newNode) return
  const nodes = fieldValues.getLinkedRecords('nodes') || []
  const updatedNodes = nodes.filter(n => n?.getLinkedRecord('field')?.getDataID() !== fieldId)
  updatedNodes.push(newNode as RecordProxy)
  fieldValues.setLinkedRecords(updatedNodes, 'nodes')
}

// RELAY CLEANUP: Remove this compatibility write when FieldsSection no longer reads issueFieldValues from the primary IssueViewer Relay query.
export function commitIssueFieldValueToRelay(
  environment: Environment,
  issueId: string,
  fieldId: string,
  value: ConfirmedFieldValue,
) {
  environment.commitUpdate(store => {
    const fieldValues = store.get(issueId)?.getLinkedRecord('issueFieldValues', {first: ISSUE_FIELD_VALUES_PAGE_SIZE})
    if (!fieldValues) return

    const node = store.get(value.id) ?? store.create(value.id, value.__typename)
    node.setValue(value.id, 'id')
    const field = store.get(fieldId)
    if (field) node.setLinkedRecord(field, 'field')

    setConfirmedValues(store, node, value)
    replaceFieldValueInCollection(fieldValues, fieldId, node)
  })
}

export function applyOptimisticIssueFieldValueToRelay(
  environment: Environment,
  input: IssueFieldValueMutationInput,
  optimisticFieldValue: OptimisticIssueFieldValue,
) {
  return environment.applyUpdate({
    storeUpdater: store => {
      const fieldValues = store
        .get(input.issueId)
        ?.getLinkedRecord('issueFieldValues', {first: ISSUE_FIELD_VALUES_PAGE_SIZE})
      if (!fieldValues) return

      const existingNode = (fieldValues.getLinkedRecords('nodes') || []).find(
        node => node?.getLinkedRecord('field')?.getDataID() === input.issueField.fieldId,
      )
      const nodeId =
        existingNode?.getDataID() || `client:issue-field-value:${input.issueId}:${input.issueField.fieldId}`
      const node = existingNode ?? store.get(nodeId) ?? store.create(nodeId, optimisticFieldValue.__typename)

      node.setValue(nodeId, 'id')
      const field = store.get(input.issueField.fieldId)
      if (field) node.setLinkedRecord(field, 'field')

      switch (optimisticFieldValue.__typename) {
        case 'IssueFieldTextValue':
        case 'IssueFieldDateValue':
          node.setValue(optimisticFieldValue.value ?? null, 'value')
          break
        case 'IssueFieldNumberValue':
          node.setValue(optimisticFieldValue.numberValue ?? null, 'value')
          break
        case 'IssueFieldSingleSelectValue':
          node.setValue(optimisticFieldValue.optionId ?? null, 'optionId')
          node.setValue(optimisticFieldValue.name ?? null, 'name')
          node.setValue(optimisticFieldValue.color ?? null, 'color')
          node.setValue(optimisticFieldValue.description ?? null, 'description')
          break
        case 'IssueFieldMultiSelectValue': {
          const options = (optimisticFieldValue.options ?? []).map((option, index) => {
            const optionId =
              option.id ?? `client:issue-field-option:${input.issueField.fieldId}:${option.name}:${index.toString()}`
            const record = store.get(optionId) ?? store.create(optionId, 'IssueFieldSingleSelectOption')
            record.setValue(optionId, 'id')
            record.setValue(option.name, 'name')
            record.setValue(option.color ?? null, 'color')
            record.setValue(option.description ?? null, 'description')
            return record
          })
          node.setLinkedRecords(options, 'options')
          break
        }
      }
      replaceFieldValueInCollection(fieldValues, input.issueField.fieldId, node)
    },
  })
}

function setConfirmedValues(
  store: RecordSourceProxy,
  node: NonNullable<ReturnType<RecordSourceSelectorProxy['get']>>,
  value: ConfirmedFieldValue,
) {
  switch (value.__typename) {
    case 'IssueFieldTextValue':
    case 'IssueFieldDateValue':
      node.setValue(value.value, 'value')
      break
    case 'IssueFieldNumberValue':
      node.setValue(value.numberValue, 'value')
      break
    case 'IssueFieldSingleSelectValue':
      node.setValue(
        'optionId' in value && (typeof value.optionId === 'string' || value.optionId === null) ? value.optionId : null,
        'optionId',
      )
      node.setValue(value.name, 'name')
      node.setValue(value.color, 'color')
      node.setValue(value.description, 'description')
      break
    case 'IssueFieldMultiSelectValue': {
      const options = value.options.map(option => {
        const record = store.get(option.id) ?? store.create(option.id, 'IssueFieldSingleSelectOption')
        record.setValue(option.id, 'id')
        record.setValue(option.name, 'name')
        record.setValue(option.color, 'color')
        record.setValue(option.description, 'description')
        return record
      })
      node.setLinkedRecords(options, 'options')
      node.setValue(value.multiSelectValue, 'value')
      break
    }
  }
}
